import { classifyMobileTouchGesture } from '@/utils/mobileTouchGesture'

const MOBILE_PREVIEW_GRID_LONG_PRESS_DELAY = 520
const MOBILE_PREVIEW_GRID_CLICK_SUPPRESS_DELAY = 450

export default {
  data() {
    return {
      mobilePreviewGridTouchState: null,
      mobilePreviewGridTouchTimer: null,
      mobilePreviewGridSuppressClickUntil: 0
    }
  },
  beforeDestroy() {
    this.cancelMobilePreviewGridTouch()
  },
  methods: {
    getMobilePreviewGridSelectionConfig(scope) {
      if (scope == 'paper-pages') {
        return {
          items: this.detailPages,
          selectedKey: 'selectedDetailPageNos',
          anchorKey: 'selectedDetailPageAnchorNo',
          getId: item => item && item.pageNo,
          openItem: item => this.openDetailPageFromGrid(item.pageNo)
        }
      }
      if (scope == 'daily-preview') {
        return {
          items: this.dailyPreviewItems,
          selectedKey: 'dailyPreviewSelectedIds',
          anchorKey: 'dailyPreviewSelectionAnchorId',
          getId: item => item && item.id,
          openItem: item => this.openDailyPreviewItem(item)
        }
      }
      if (scope == 'learning-annotation') {
        return {
          items: this.learningAnnotations,
          selectedKey: 'learningAnnotationPreviewSelectedIds',
          anchorKey: 'learningAnnotationPreviewSelectionAnchorId',
          getId: item => item && item.id,
          openItem: item => this.openLearningAnnotationFromPreviewGrid(item)
        }
      }
      if (scope == 'recent-learning-annotation') {
        return {
          items: this.recentLearningAnnotations,
          selectedKey: 'recentLearningAnnotationPreviewSelectedIds',
          anchorKey: 'recentLearningAnnotationPreviewSelectionAnchorId',
          getId: item => item && item.id,
          openItem: item => this.openRecentLearningAnnotationFromPreviewGrid(item)
        }
      }
      return null
    },
    normalizeMobilePreviewGridSelectionConfig(scope) {
      const config = this.getMobilePreviewGridSelectionConfig(scope)
      if (!config || !Array.isArray(config.items) || typeof config.getId != 'function' || typeof config.openItem != 'function') {
        return null
      }
      if (!config.selectedKey || !config.anchorKey || !Object.prototype.hasOwnProperty.call(this, config.selectedKey)) {
        return null
      }
      return config
    },
    findMobilePreviewGridItem(config, itemId) {
      return config.items.find(item => String(config.getId(item)) == String(itemId)) || null
    },
    setMobilePreviewGridSelection(config, selectedIds, anchorId = '') {
      this[config.selectedKey] = Array.isArray(selectedIds) ? selectedIds : []
      this[config.anchorKey] = anchorId
    },
    toggleMobilePreviewGridSelection(config, item) {
      const id = config.getId(item)
      if (id === undefined || id === null || id === '') {
        return
      }
      const selectedIds = Array.isArray(this[config.selectedKey]) ? this[config.selectedKey].slice() : []
      const selectedIndex = selectedIds.findIndex(selectedId => String(selectedId) == String(id))
      if (selectedIndex >= 0) {
        selectedIds.splice(selectedIndex, 1)
      } else {
        selectedIds.push(id)
      }
      this.setMobilePreviewGridSelection(config, selectedIds, selectedIndex >= 0 ? (selectedIds[0] || '') : id)
    },
    applyMobilePreviewGridLongPressSelection(config, item, selectionMode = '') {
      if (!config || !item) {
        return
      }
      const id = config.getId(item)
      if (id === undefined || id === null || id === '') {
        return
      }
      const currentIds = Array.isArray(this[config.selectedKey]) ? this[config.selectedKey].slice() : []
      const shouldSelect = selectionMode == 'select'
        ? true
        : selectionMode == 'deselect'
          ? false
          : !currentIds.some(selectedId => String(selectedId) == String(id))
      const selectedSet = new Set(currentIds.map(selectedId => String(selectedId)))
      if (shouldSelect) {
        if (!selectedSet.has(String(id))) {
          currentIds.push(id)
        }
      } else {
        const selectedIndex = currentIds.findIndex(selectedId => String(selectedId) == String(id))
        if (selectedIndex >= 0) {
          currentIds.splice(selectedIndex, 1)
        }
      }
      this.setMobilePreviewGridSelection(config, currentIds, currentIds.length > 0 ? id : '')
    },
    startMobilePreviewGridTouch(scope, itemId, event) {
      this.clearMobilePreviewGridTouchTimer()
      this.clearMobilePreviewGridClickSuppression()
      const config = this.normalizeMobilePreviewGridSelectionConfig(scope)
      const item = config && this.findMobilePreviewGridItem(config, itemId)
      const touch = event && event.touches && event.touches[0]
      if (!item || !touch || typeof window === 'undefined') {
        this.mobilePreviewGridTouchState = null
        return
      }
      const normalizedId = config.getId(item)
      const selectedIds = Array.isArray(this[config.selectedKey]) ? this[config.selectedKey] : []
      const selectionMode = selectedIds.some(selectedId => String(selectedId) == String(normalizedId))
        ? 'deselect'
        : 'select'
      this.mobilePreviewGridTouchState = {
        scope,
        startId: normalizedId,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        selectionMode,
        selectionWasActive: selectedIds.length > 0,
        moved: false,
        longPressTriggered: false,
        selectionStarted: false,
        gestureType: 'pending'
      }
      this.mobilePreviewGridTouchTimer = window.setTimeout(() => {
        this.mobilePreviewGridTouchTimer = null
        const state = this.mobilePreviewGridTouchState
        if (!state || state.scope != scope || String(state.startId) != String(normalizedId) || state.moved) {
          return
        }
        state.longPressTriggered = true
        state.selectionStarted = true
        state.gestureType = 'selection'
        this.applyMobilePreviewGridLongPressSelection(config, item, state.selectionMode)
        this.setMobilePreviewGridClickSuppression()
      }, MOBILE_PREVIEW_GRID_LONG_PRESS_DELAY)
    },
    moveMobilePreviewGridTouch(event) {
      const state = this.mobilePreviewGridTouchState
      const touch = event && event.touches && event.touches[0]
      if (!state || !touch) {
        return
      }
      const gestureType = classifyMobileTouchGesture(state, touch, { pullEligible: false })
      if (gestureType == 'pending') {
        return
      }
      state.moved = true
      state.gestureType = gestureType
      this.clearMobilePreviewGridTouchTimer()
      if (gestureType != 'selection') {
        return
      }
      if (!state.longPressTriggered) {
        if (!state.selectionWasActive) {
          return
        }
        state.longPressTriggered = true
        state.selectionStarted = true
        const config = this.normalizeMobilePreviewGridSelectionConfig(state.scope)
        const startItem = config && this.findMobilePreviewGridItem(config, state.startId)
        this.applyMobilePreviewGridLongPressSelection(config, startItem, state.selectionMode)
        this.setMobilePreviewGridClickSuppression()
      }
      if (event && event.cancelable) {
        event.preventDefault()
      }
      this.extendMobilePreviewGridSelection(state, touch.clientX, touch.clientY)
    },
    extendMobilePreviewGridSelection(state, clientX, clientY) {
      if (typeof document === 'undefined' || typeof document.elementFromPoint != 'function') {
        return
      }
      const element = document.elementFromPoint(clientX, clientY)
      const button = element && typeof element.closest == 'function' ? element.closest('.mobile-preview-grid-item') : null
      if (!button || button.getAttribute('data-preview-grid-scope') != state.scope) {
        return
      }
      const config = this.normalizeMobilePreviewGridSelectionConfig(state.scope)
      const targetItem = config && this.findMobilePreviewGridItem(config, button.getAttribute('data-preview-grid-id'))
      if (!targetItem) {
        return
      }
      const startIndex = config.items.findIndex(item => String(config.getId(item)) == String(state.startId))
      const endIndex = config.items.indexOf(targetItem)
      if (startIndex < 0 || endIndex < 0) {
        return
      }
      const selectedIds = Array.isArray(this[config.selectedKey]) ? this[config.selectedKey].slice() : []
      const selectedSet = new Set(selectedIds.map(selectedId => String(selectedId)))
      const shouldSelect = state.selectionMode != 'deselect'
      config.items.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1).forEach(item => {
        const id = config.getId(item)
        if (id === undefined || id === null || id === '') {
          return
        }
        if (shouldSelect && !selectedSet.has(String(id))) {
          selectedIds.push(id)
          selectedSet.add(String(id))
        } else if (!shouldSelect && selectedSet.has(String(id))) {
          const selectedIndex = selectedIds.findIndex(selectedId => String(selectedId) == String(id))
          if (selectedIndex >= 0) {
            selectedIds.splice(selectedIndex, 1)
          }
          selectedSet.delete(String(id))
        }
      })
      this.setMobilePreviewGridSelection(config, selectedIds, selectedIds.length > 0 ? config.getId(targetItem) : '')
    },
    endMobilePreviewGridTouch() {
      this.clearMobilePreviewGridTouchTimer()
      const state = this.mobilePreviewGridTouchState
      if (state && (state.longPressTriggered || state.selectionStarted || state.moved)) {
        this.setMobilePreviewGridClickSuppression()
      }
      this.mobilePreviewGridTouchState = null
    },
    cancelMobilePreviewGridTouch() {
      this.clearMobilePreviewGridTouchTimer()
      const state = this.mobilePreviewGridTouchState
      if (state && (state.longPressTriggered || state.selectionStarted || state.moved)) {
        this.setMobilePreviewGridClickSuppression()
      }
      this.mobilePreviewGridTouchState = null
    },
    handleMobilePreviewGridContextMenu(scope, itemId) {
      this.clearMobilePreviewGridTouchTimer()
      const config = this.normalizeMobilePreviewGridSelectionConfig(scope)
      const item = config && this.findMobilePreviewGridItem(config, itemId)
      if (!item) {
        return
      }
      if (this.isMobilePreviewGridClickSuppressed()) {
        if (this.mobilePreviewGridTouchState) {
          this.mobilePreviewGridTouchState.longPressTriggered = true
        }
        return
      }
      if (this.mobilePreviewGridTouchState && this.mobilePreviewGridTouchState.moved && !this.mobilePreviewGridTouchState.longPressTriggered) {
        this.setMobilePreviewGridClickSuppression()
        return
      }
      const alreadyTriggered = !!(this.mobilePreviewGridTouchState && this.mobilePreviewGridTouchState.longPressTriggered)
      if (this.mobilePreviewGridTouchState) {
        this.mobilePreviewGridTouchState.longPressTriggered = true
        this.mobilePreviewGridTouchState.selectionStarted = true
        this.mobilePreviewGridTouchState.gestureType = 'selection'
      }
      if (!alreadyTriggered) {
        const selectedIds = Array.isArray(this[config.selectedKey]) ? this[config.selectedKey] : []
        const selectionMode = selectedIds.some(selectedId => String(selectedId) == String(itemId))
          ? 'deselect'
          : 'select'
        this.applyMobilePreviewGridLongPressSelection(config, item, selectionMode)
      }
      this.setMobilePreviewGridClickSuppression()
    },
    handleMobilePreviewGridItemClick(scope, itemId) {
      if (this.isMobilePreviewGridClickSuppressed()) {
        this.clearMobilePreviewGridClickSuppression()
        return
      }
      const config = this.normalizeMobilePreviewGridSelectionConfig(scope)
      const item = config && this.findMobilePreviewGridItem(config, itemId)
      if (!item) {
        return
      }
      const selectedIds = Array.isArray(this[config.selectedKey]) ? this[config.selectedKey] : []
      if (selectedIds.length > 0) {
        this.toggleMobilePreviewGridSelection(config, item)
        return
      }
      config.openItem(item)
    },
    clearMobilePreviewGridTouchTimer() {
      if (this.mobilePreviewGridTouchTimer) {
        clearTimeout(this.mobilePreviewGridTouchTimer)
        this.mobilePreviewGridTouchTimer = null
      }
    },
    setMobilePreviewGridClickSuppression() {
      this.mobilePreviewGridSuppressClickUntil = Date.now() + MOBILE_PREVIEW_GRID_CLICK_SUPPRESS_DELAY
    },
    isMobilePreviewGridClickSuppressed() {
      if (Date.now() >= this.mobilePreviewGridSuppressClickUntil) {
        this.clearMobilePreviewGridClickSuppression()
        return false
      }
      return true
    },
    clearMobilePreviewGridClickSuppression() {
      this.mobilePreviewGridSuppressClickUntil = 0
    }
  }
}
